import React, { useState, useMemo, useRef, useEffect } from "react";
import { ReactComponent as BottleIcon } from "assets/images/bottle.svg";
import { ReactComponent as Chevron16Icon } from "assets/images/chevron16.svg";
import { SubjectRecommendationsProps } from "./types";

import * as S from "./styles";

const SubjectRecommendations: React.FC<SubjectRecommendationsProps> = ({
  allSubjects,
  subjectRecommendations,
  selectedCareer,
  favouriteCareers,
}) => {
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState<Set<string>>(
    new Set()
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLegendClick = (type: "required" | "recommended" | "starred") => {
    if (selectedSubject === type) {
      setSelectedSubject(null);
    } else {
      setSelectedSubject(type);
    }
  };

  const handleSubjectToggle = (subjectId: string) => {
    const newSelectedSubjects = new Set(selectedSubjects);
    if (newSelectedSubjects.has(subjectId)) {
      newSelectedSubjects.delete(subjectId);
    } else {
      newSelectedSubjects.add(subjectId);
    }
    setSelectedSubjects(newSelectedSubjects);
  };

  // Calculate subject counts
  const subjectCounts = useMemo(() => {
    const counts: { [subjectId: string]: number } = {};

    allSubjects?.forEach((subject) => {
      counts[subject.id] = 0;
    });

    subjectRecommendations.forEach((career) => {
      [...career.requiredSubjects, ...career.recommendedSubjects].forEach(
        (subject) => {
          if (counts[subject.id] !== undefined) {
            counts[subject.id] += 1;
          }
        }
      );
    });

    return counts;
  }, [allSubjects, subjectRecommendations]);

  // // Filter subjects based on search term
  // const filteredSubjects = useMemo(() => {
  //   if (!allSubjects) return [];

  //   return allSubjects.filter(
  //     (subject) =>
  //       subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       (subject.shortName &&
  //         subject.shortName.toLowerCase().includes(searchTerm.toLowerCase()))
  //   );
  // }, [allSubjects, searchTerm]);

  // Filter displayed subjects in table based on selected subjects
  const displayedSubjects = useMemo(() => {
    if (!allSubjects) return [];

    if (selectedSubjects.size === 0) {
      return allSubjects;
    }

    return allSubjects.filter((subject) => selectedSubjects.has(subject.id));
  }, [allSubjects, selectedSubjects]);

  return (
    <S.Container>
      <S.Header singleLine={!!selectedCareer}>
        <S.TopSection>
          <S.Title>Subject Recommendations</S.Title>
        </S.TopSection>

        {selectedCareer && (
          <S.Description>
            These subjects are typically required or recommended, however we
            advise that you check the specific requirements for the courses you
            are interested in pursuing directly.
          </S.Description>
        )}

        <S.Legend>
          <S.LegendItem
            onClick={() => handleLegendClick("required")}
            singleLine={!!selectedCareer}
            style={{
              borderColor: selectedSubject === "required" ? "#E91E63" : "",
              color: selectedSubject === "required" ? "#E91E63" : "",
            }}
          >
            <S.LegendCircle type="required" />
            Required
          </S.LegendItem>
          <S.LegendItem
            onClick={() => handleLegendClick("recommended")}
            singleLine={!!selectedCareer}
            style={{
              borderColor: selectedSubject === "recommended" ? "#FF9800" : "",
              color: selectedSubject === "recommended" ? "#FF9800" : "",
            }}
          >
            <S.LegendCircle type="recommended" />
            Recommended
          </S.LegendItem>
          {favouriteCareers.length > 0 && (
            <S.LegendItem
              onClick={() => handleLegendClick("starred")}
              singleLine={!!selectedCareer}
              style={{
                borderColor: selectedSubject === "starred" ? "#FF9800" : "",
                color: selectedSubject === "starred" ? "#FF9800" : "",
              }}
            >
              <S.StarIcon />
              Favourite Careers
            </S.LegendItem>
          )}
        </S.Legend>
        {!selectedCareer && (
          <S.FilterDropdownContainer ref={dropdownRef}>
            <S.FilterDropdownButton
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              type="button"
            >
              <BottleIcon />
              Subject
              <Chevron16Icon />
            </S.FilterDropdownButton>

            <S.FilterDropdownContent isOpen={isDropdownOpen}>
              <S.FilterHeader>
                <S.FilterTitle>Filter by Subject</S.FilterTitle>
              </S.FilterHeader>

              <S.FilterList>
                {allSubjects?.map((subject) => (
                  <S.FilterItem key={subject.id}>
                    <S.Checkbox
                      type="checkbox"
                      id={`subject-${subject.id}`}
                      checked={selectedSubjects.has(subject.id)}
                      onChange={() => handleSubjectToggle(subject.id)}
                    />
                    <S.SubjectLabel htmlFor={`subject-${subject.id}`}>
                      {subject.name} ({subjectCounts[subject.id] || 0})
                    </S.SubjectLabel>
                  </S.FilterItem>
                ))}
              </S.FilterList>
            </S.FilterDropdownContent>
          </S.FilterDropdownContainer>
        )}
      </S.Header>

      <S.TableContainer>
        <S.Table>
          <thead>
            <S.HeaderRow>
              {!selectedCareer && (
                <S.HeaderCell mobileShow isSmall={!!selectedCareer} />
              )}
              {allSubjects?.map((subject) => (
                <S.HeaderCell
                  key={subject.id}
                  isSmall={!!selectedCareer}
                  style={{
                    position: selectedCareer ? "relative" : "sticky",
                  }}
                  mobileShow={
                    (selectedSubjects.size > 0 &&
                      !!selectedSubjects.has(subject.id)) ||
                    selectedSubjects.size === 0
                  }
                >
                  <S.RotatedText>
                    {subject.shortName || subject.name}{" "}
                  </S.RotatedText>
                </S.HeaderCell>
              ))}
            </S.HeaderRow>
          </thead>
          <tbody>
            {subjectRecommendations
              ?.filter((career) =>
                selectedCareer ? career.careerName === selectedCareer : career
              )
              .filter((career) =>
                selectedSubjects.size === 0
                  ? true
                  : career.requiredSubjects.some((s) =>
                      selectedSubjects.has(s.id)
                    ) ||
                    career.recommendedSubjects.some((s) =>
                      selectedSubjects.has(s.id)
                    )
              )
              .filter((career) =>
                selectedSubject === "starred"
                  ? favouriteCareers.some(
                      (f) => Number(f.id) === Number(career.careerId)
                    )
                  : true
              )
              .map((career, index) => (
                <S.CareerRow key={career.careerId} isEven={index % 2 === 0}>
                  {!selectedCareer && (
                    <S.CareerNameCell>{career.careerName}</S.CareerNameCell>
                  )}
                  {allSubjects?.map((subject) => {
                    const isRequired = career.requiredSubjects.some(
                      (s) => s.id === subject.id
                    );
                    const isRecommended = career.recommendedSubjects.some(
                      (s) => s.id === subject.id
                    );

                    return (
                      <S.SubjectCell
                        key={subject.id}
                        mobileShow={
                          (selectedSubjects.size > 0 &&
                            !!selectedSubjects.has(subject.id)) ||
                          selectedSubjects.size === 0
                        }
                      >
                        {isRequired &&
                          (displayedSubjects === allSubjects ||
                            selectedSubjects.has(subject.id)) &&
                          (selectedSubject === "required" ||
                            selectedSubject === null ||
                            selectedSubject === "starred") && (
                            <S.RequirementCircle type="required" />
                          )}
                        {isRecommended &&
                          !isRequired &&
                          (displayedSubjects === allSubjects ||
                            selectedSubjects.has(subject.id)) &&
                          (selectedSubject === "recommended" ||
                            selectedSubject === null ||
                            selectedSubject === "starred") && (
                            <S.RequirementCircle type="recommended" />
                          )}
                      </S.SubjectCell>
                    );
                  })}
                </S.CareerRow>
              ))}
          </tbody>
        </S.Table>
      </S.TableContainer>
    </S.Container>
  );
};

export default SubjectRecommendations;
